import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Survives full page reload when cross-origin HttpOnly cookies are fragile (localhost vs 127.0.0.1, etc.). */
export const ADMIN_ACCESS_TOKEN_KEY = "gv_admin_access_token";

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Important: send/receive cookies
});

// Add CSRF token to requests (for state-changing operations)
let csrfToken = null;

export const setCsrfToken = (token) => {
  csrfToken = token;
};

/** Prefer the csrf_token cookie so the header always matches what the server set on login. */
function getCsrfTokenForRequest() {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  if (match) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }
  return csrfToken;
}

api.interceptors.request.use((config) => {
  const bearer = sessionStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
  if (bearer && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${bearer}`;
  }
  // Add CSRF token for state-changing requests (double-submit: header must match cookie)
  const method = (config.method || "get").toLowerCase();
  if (method !== "get" && method !== "head") {
    const token = getCsrfTokenForRequest();
    if (token) {
      config.headers["X-CSRF-Token"] = token;
      csrfToken = token;
    }
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem(ADMIN_ACCESS_TOKEN_KEY);
      // Don't redirect if we're already on the login page
      if (!window.location.pathname.includes('/login')) {
        localStorage.removeItem("gv_admin");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

// Auth APIs
export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  getMe: () => api.get("/auth/me"),
  logout: () => api.post("/auth/logout"),
  getCsrfToken: () => api.get("/auth/csrf-token"),
};

// Public APIs
export const publicApi = {
  getOrigins: () => api.get("/public/origins"),
  getWeaveTypes: (originId) =>
    api.get("/public/weave-types", { params: { origin_id: originId } }),
  getColors: () => api.get("/public/colors"),
  getFabrics: () => api.get("/public/fabrics"),
  getOccasions: () => api.get("/public/occasions"),
  getTags: () => api.get("/public/tags"),
  getProducts: (params) => api.get("/public/products", { params }),
  getProduct: (id) => api.get(`/public/products/${id}`),
  getStates: () => api.get("/public/states"),
  getCities: (stateId) => api.get(`/public/cities?state_id=${stateId}`),
};

// Admin APIs
export const adminApi = {
  // Assets
  uploadAsset: (formData) =>
    api.post("/admin/assets", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  // Dashboard
  getDashboardStats: () => api.get("/admin/dashboard/stats"),
  getAnalytics: (period) =>
    api.get("/admin/dashboard/analytics", { params: { period } }),

  // Origins
  createOrigin: (data) => api.post("/admin/origins", data),
  updateOrigin: (id, data) => api.put(`/admin/origins/${id}`, data),
  deleteOrigin: (id) => api.delete(`/admin/origins/${id}`),

  // Weave Types
  createWeaveType: (data) => api.post("/admin/weave-types", data),
  updateWeaveType: (id, data) => api.put(`/admin/weave-types/${id}`, data),
  deleteWeaveType: (id) => api.delete(`/admin/weave-types/${id}`),

  // Colors
  createColor: (data) => api.post("/admin/colors", data),
  updateColor: (id, data) => api.put(`/admin/colors/${id}`, data),
  deleteColor: (id) => api.delete(`/admin/colors/${id}`),

  // Fabrics
  createFabric: (data) => api.post("/admin/fabrics", data),
  updateFabric: (id, data) => api.put(`/admin/fabrics/${id}`, data),
  deleteFabric: (id) => api.delete(`/admin/fabrics/${id}`),

  // Occasions
  createOccasion: (data) => api.post("/admin/occasions", data),
  updateOccasion: (id, data) => api.put(`/admin/occasions/${id}`, data),
  deleteOccasion: (id) => api.delete(`/admin/occasions/${id}`),

  // Tags
  createTag: (data) => api.post("/admin/tags", data),
  updateTag: (id, data) => api.put(`/admin/tags/${id}`, data),
  deleteTag: (id) => api.delete(`/admin/tags/${id}`),

  // Products
  getProducts: (params) => api.get("/admin/products", { params }),
  createProduct: (data) => api.post("/admin/products", data),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  uploadProductImage: (productId, colorVariantIndex, file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("color_variant_index", colorVariantIndex);
    return api.post(`/admin/products/${productId}/images`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  deleteProductImage: (productId, colorVariantIndex, imageUrl) =>
    api.delete(`/admin/products/${productId}/images`, {
      params: { color_variant_index: colorVariantIndex, image_url: imageUrl },
    }),

  // Customers
  getCustomers: (params) => api.get("/admin/customers", { params }),
  updateCustomer: (id, data) => api.put(`/admin/customers/${id}`, data),
  makeCustomerAdmin: (id) => api.post(`/admin/customers/${id}/make-admin`),

  // Carts
  getCarts: (params) => api.get("/admin/carts", { params }),

  // Wishlists
  getWishlists: (params) => api.get("/admin/wishlists", { params }),

  // Orders
  getOrders: (params) => api.get("/admin/orders", { params }),
  updateOrderStatus: (id, data) => api.put(`/admin/orders/${id}/status`, data),

  // Coupons
  getCoupons: (params) => api.get("/admin/coupons", { params }),
  createCoupon: (data) => api.post("/admin/coupons", data),
  updateCoupon: (id, data) => api.put(`/admin/coupons/${id}`, data),
  deleteCoupon: (id) => api.delete(`/admin/coupons/${id}`),

  // Shipping
  getShippingConfig: () => api.get("/admin/shipping/config"),
  updateShippingConfig: (data) => api.put("/admin/shipping/config", data),
};

export default api;
